import { Dialog, DialogContent, Grid, Box, DialogActions, Card, useMediaQuery } from "@mui/material";
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { FlatButton } from "src/framework/components/buttons/FlatButton";
import { AnexoFile, UploadFileDocumentoServico } from "./UploadFileDocumentoServico";
import { DocumentosServicos, DocumentosServicosArquivo, Projeto, STATUS_DOCUMENTO, TDadosBancariosConta } from "../../types";
import api, { IDataRequest, STATUS_CODE } from "src/framework/providers/api";
import { DEFAULT_STATUS_COMISSIONAMENTO, DOCUMENTOS_SERVICO_TYPE, STATUS_COMISSIONAMENTO } from "../../constants";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { InputDateMui } from "src/framework/components/forms/inputs/InputDateMui";
import { DownloadUtil } from "src/framework/commons/DownloadUtil";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import theme from "src/theme/light";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CollectionUtil } from "src/framework/commons/CollectionUtil";
import moment from "moment";
import { MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO } from "src/App";
import "./styles.css";
import { notifierStore } from "src/framework/store/NotifierStore";

export interface ModalEnviarDocumentosServicoProperties {
    openModal: boolean;
    projeto: Projeto,
    onClose: () => void;
    onSuccess: ()=>void;
}

interface Document {
    tipo: DOCUMENTOS_SERVICO_TYPE;
    documentos: { nome: string }[],
    status_geral: STATUS_DOCUMENTO
    label: string
    tooltipText: string
}

interface IndexDocuments {
    fileType: string;
    extension: string;
    count: number;
}

const configurationsByTipo = {
    [DOCUMENTOS_SERVICO_TYPE.ORDEM_SERVICO]: {
        tooltipText: "Ordem de serviço preenchida e assinada pelo cliente,no mesmo endereço da ART/TRT/CRT (este documento se encontra plataforma > biblioteca > Ordem de Serviço) para execução de projeto de Sistema Fotovoltaico",
        order: 1,
        label: "Ordem de Serviço",
    },
    [DOCUMENTOS_SERVICO_TYPE.NF_SERVICO_INSTALACAO]: {
        tooltipText: "Nota de serviço (valor líquido da Nota Fiscal deve ser liquido sem PIS/COFINS). Código de atividade 43.21-500 ou 33.21-000, código de serviço 14.06 ou, caso não tenha o 14.06 utilizar o 14.01",
        order: 2,
        label: "Nota Fiscal de Serviço e Instalação",
    },
    [DOCUMENTOS_SERVICO_TYPE.IDENTIFICACAO_CLIENTE]: {
        tooltipText: "Documento com foto frente e verso do cliente, se a pessoa jurídica, contrato empresarial reconhecido em cartório",
        order: 3,
        label: "Foto do Documento de Identificação do Cliente (RG/CNH)",
    },
    [DOCUMENTOS_SERVICO_TYPE.ART_TRT_PROJETO]: {
        tooltipText: "ART/TRT/CRT de execução no endereço da obra, assinada pelo responsável",
        order: 4,
        label: "ART/TRT do Projeto",
    },
}

const TEXTO_FATURAMENTO = "Atenção ao emitir a NF do serviço: o tomador do serviço precisa ser o mesmo CNPJ que emitiu a NF do Gerador."

const STATUS_COMISSIONAMENTO_LISTA = STATUS_COMISSIONAMENTO.filter(item => item.value !== "aguardando_finalizacao");

const ModalEnviarDocumentosServico: FC<ModalEnviarDocumentosServicoProperties> = ({
    openModal,
    projeto,
    onClose,
    onSuccess,
}) => {
    // Permissions
    const permissions = PermissionUtil.getPermissions();

    const [ordemServicoFiles, setOrdemServicoFiles] = useState<AnexoFile[]>([]);
    const [notaFiscalServicoInstalacaoFiles, setNotaFiscalServicoInstalacaoFiles] = useState<AnexoFile[]>([]);
    const [idenficacaoClienteFiles, setIdenficacaoClienteFiles] = useState<AnexoFile[]>([]);
    const [artTrtFiles, setArtTrtFiles] = useState<AnexoFile[]>([]);
    const [permiteGerenciamentoPagamento, setPermiteGerenciamentoPagamento] = useState(false);
    const [dadosBancarios, setDadosBancarios] = useState<{ status: boolean, message: ReactNode } | null>(null);
    const [selectStatusComissionamento, setSelectStatusComissionamento] = useState<{ text: string, value: string } | null>(null);
    const [dataPagamentoComissionamento, setDataPagamentoComissionamento] = useState<string>(projeto?.data_pagamento_comissionamento || "");

    const faturamentoRenovigi = !!projeto?.dados_finalizar_renovigi;

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const FooterBottom = isMobile ? Box : DialogActions;

    const documentos = useMemo((): Document[] => {
        const docsProjeto = projeto?.documentos_servico?.length ? projeto.documentos_servico : []

        const result: Document[] = []

        Object.values(DOCUMENTOS_SERVICO_TYPE).forEach(tipoDoc => {
            const uploadedDocs = docsProjeto.filter(item => item.tipo === tipoDoc);

            const getStatusGeral = (): STATUS_DOCUMENTO => {
                if (!uploadedDocs.length) return STATUS_DOCUMENTO.PENDENTE
                if (uploadedDocs.every(item => item.status_documento === STATUS_DOCUMENTO.APROVADO)) return STATUS_DOCUMENTO.APROVADO
                const tipoPendente = uploadedDocs.some(item => item.status_documento === STATUS_DOCUMENTO.PENDENTE)
                const tipoRecusado = uploadedDocs.some(item => item.status_documento === STATUS_DOCUMENTO.RECUSADO)
                if (tipoRecusado && !tipoPendente) return STATUS_DOCUMENTO.RECUSADO
                return STATUS_DOCUMENTO.PENDENTE
            }

            let pular = false;

            if(tipoDoc === DOCUMENTOS_SERVICO_TYPE.ART_TRT_PROJETO) {
                if ((projeto?.tipo === "dimensionado" || projeto?.tipo === "personalizado" ) && projeto?.segmento === "distribuicao_offgrid") {
                    pular = true;
                }

                if(projeto?.tipo === "kit" && (projeto?.segmento === "distribuicao_offgrid" || projeto?.segmento === "vertical_offgrid")) {
                    pular = true;
                }
            }

            if (!pular) {
                result.push({
                    tipo: tipoDoc,
                    label: configurationsByTipo[tipoDoc].label,
                    tooltipText: configurationsByTipo[tipoDoc].tooltipText,
                    documentos: uploadedDocs.map(item => ({
                        nome: item.url.split("/").pop() || "",
                    })).sort(CollectionUtil.singleSort("nome")),
                    status_geral: getStatusGeral()
                })
            }
        })

        return result

    }, [projeto?.documentos_servico])

    const isValidDate = (dateString: any, minimumDate?: boolean) =>  {
        if (!dateString && !minimumDate) return true;
        const date = new Date(dateString);
        const valid = !isNaN(date.getTime())
        if (valid && minimumDate) {
            const selectDate = moment(date).format("YYYY-MM-DD");
            const currentDate = moment(new Date()).format("YYYY-MM-DD");
            return (selectDate >= currentDate);
        }
        return valid;
    }

    const projetoSemArtTrt = () => {
        if ((projeto?.tipo === "dimensionado" || projeto?.tipo === "personalizado") && projeto?.segmento === "distribuicao_offgrid") {
            return true;
        }

        if(projeto?.tipo === "kit" && (projeto?.segmento === "distribuicao_offgrid" || projeto?.segmento === "vertical_offgrid")) {
            return true;
        }

        return false;
    }
    
    const canSubmit = useMemo(() => {
        // Faturamento pela Renovigi
        if (faturamentoRenovigi) {
            if (projeto?.documentos_servico?.length) {
                const dataValida = isValidDate(dataPagamentoComissionamento);
                if ((selectStatusComissionamento?.value !== projeto?.status_comissionamento) 
                    || (projeto?.data_pagamento_comissionamento !== dataPagamentoComissionamento && dataValida)) 
                {
                    return true;
                }
            }
            if (!ordemServicoFiles.length || !notaFiscalServicoInstalacaoFiles.length || !idenficacaoClienteFiles.length || (!artTrtFiles.length && projetoSemArtTrt() === false)) {
                return false;
            }
            return true;
        }

        if (!projeto?.documentos_servico?.length && ordemServicoFiles.length && notaFiscalServicoInstalacaoFiles.length && idenficacaoClienteFiles.length && (artTrtFiles.length || projetoSemArtTrt())) return true

        if (
            !documentos.some(doc => doc.status_geral === 'recusado') ||
            documentos.every(doc => doc.status_geral === 'aprovado')        
        ) return false

        if(projetoSemArtTrt()) {
            if (
                documentos.some(doc => doc.status_geral === 'recusado') && (
                    !ordemServicoFiles.length && !notaFiscalServicoInstalacaoFiles.length && !idenficacaoClienteFiles.length
                )
            ) {
                return false
            }
        } else {
            if (
                documentos.some(doc => doc.status_geral === 'recusado') && (
                    !ordemServicoFiles.length && !notaFiscalServicoInstalacaoFiles.length && !idenficacaoClienteFiles.length && !artTrtFiles.length
                )
            ) {
                return false
            }
        }

        return true;
    }, [artTrtFiles.length,
        documentos,
        idenficacaoClienteFiles.length,
        notaFiscalServicoInstalacaoFiles.length,
        ordemServicoFiles.length,
        projeto?.documentos_servico?.length,
        projeto?.dados_finalizar_renovigi,
        projeto?.status_comissionamento,
        projeto?.data_pagamento_comissionamento,
        selectStatusComissionamento,
        dataPagamentoComissionamento,
    ])

    const statusComissao = useMemo(()=>{
        const statusComissionamento = projeto?.status_comissionamento || ""
        if (!statusComissionamento) {
            return {
                text: "Aguardando Finalização do Projeto",
                value: "aguardando_finalizacao",
            }
        }

        return STATUS_COMISSIONAMENTO.find(item => item.value === (statusComissionamento || DEFAULT_STATUS_COMISSIONAMENTO.value));
    }, [projeto?.status_comissionamento])

    const doDownload = async (): Promise<void> => {
        const request: IDataRequest = {
            url: `/projetos/documentos_servico/${projeto.id}/downloads`,
        }

        const response = await api.getFile(request);
        if (response?.statusCode === STATUS_CODE.OK && response?.data) {
            DownloadUtil.zip(response.data, projeto.codigo_venda + "-Documentos-Servico");
        } else {
            if (response?.message) {
                notifierStore.setNotifier({
                    title: "Aviso!!!",
                    content: "Não foi possível baixar os documentos.",
                    timeOut: 3000,
                    onClose: () => {
                        notifierStore.setOpen(false);
                    }
                });
                notifierStore.setOpen(true);
            }
        }
    }

    const carregarDadosBancarios = async (): Promise<void> => {
        const cnpjCpf = projeto?.revenda?.cnpj_cpf || projeto?.distribuidor?.cnpj_cpf || "";
        const request: IDataRequest = {
            url: cnpjCpf ? `/cadastros/contas/dados_bancarios?cnpj_cpf=${cnpjCpf}` : "/cadastros/contas/dados_bancarios",
        }

        const response = await api.get(request);

        if (response.statusCode === STATUS_CODE.OK) {
            if(response.data){
                const data: TDadosBancariosConta = response.data;

                if (data?.numero_banco && data?.numero_conta) {       
                    setDadosBancarios({
                        message: (
                            <>
                                <span style={{color: theme.palette.primary.main}}>Banco {data.numero_banco} - AG {data.numero_agencia} - CONTA {data.numero_conta}</span><br /> Caso sua conta jurídica esteja incorreta, favor enviar e-mail para<br /> <span style={{color: theme.palette.primary.main}}>contasapagar@intelbras.com.br</span> solicitando atualização. Reforçamos que a conta informada deverá ser vinculada ao CNPJ da NF.
                            </>
                        ),
                        status: true,
                    });
                    return
                }
            }
        }

        setDadosBancarios({
            message: <span>Dados bancários não foram cadastrados, favor enviar e-mail para<br /> <span style={{color: theme.palette.primary.main}}>contasapagar@intelbras.com.br</span> solicitando atualização. Reforçamos que a conta informada deverá ser vinculada ao CNPJ da NF.</span>,
            status: false,
        })
    }

    const findMultiplos = (files:any)=>{
        let multiplos: any[] = [];
        for (const arr of files){
            if(arr?.length>1){
                multiplos.push(arr[0]?.fileType)
            }
        }
        return multiplos;
    }
    const upload = async (): Promise<void> => {
        const form = new FormData();
        const url_post: string = `/projetos/documentos_servico/${projeto.codigo_venda}/uploads`;
        const url_post_documento: string = `/projetos/${projeto.codigo_venda}/documentos_servico/enviar`;
        let multiplos = findMultiplos([ordemServicoFiles, notaFiscalServicoInstalacaoFiles, idenficacaoClienteFiles, artTrtFiles]);

        // Faturamento pela Renovigi
        if (faturamentoRenovigi) {
            const validar = selectStatusComissionamento?.value === "documentacao_aprovada";
            const dataValida = isValidDate(dataPagamentoComissionamento, validar);
            if (!dataValida) {
                notifierStore.setNotifier({
                    title: "Aviso!",
                    content: `Data de pagamento não pode ser anterior ao dia atual.`,
                    timeOut: 3500,
                    onFinishTimeout: () => {
                        notifierStore.setOpen(false)
                    },
                    onClose: () => {
                        notifierStore.setOpen(false)
                    }
                })
                notifierStore.setOpen(true);
                return;
            }
        }

        const documentos: AnexoFile[] = [];

        if (ordemServicoFiles) {
            for await (const file of ordemServicoFiles) {
                parseFile(file, form);
            }

            documentos.push(...ordemServicoFiles);
        }

        if (notaFiscalServicoInstalacaoFiles) {
            for await (const file of notaFiscalServicoInstalacaoFiles) {
                parseFile(file, form);
            }

            documentos.push(...notaFiscalServicoInstalacaoFiles);
        }

        if (idenficacaoClienteFiles) {
            for await (const file of idenficacaoClienteFiles) {
                parseFile(file, form);
            }

            documentos.push(...idenficacaoClienteFiles);
        }

        if (artTrtFiles) {
            for await (const file of artTrtFiles) {
                parseFile(file, form);
            }

            documentos.push(...artTrtFiles);
        }

        const request: IDataRequest = {
            url: url_post,
            data: form,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }

        const response = await api.post(request);

        if (response.statusCode === STATUS_CODE.CREATED) {
            const indexes: IndexDocuments[] = [];

            const documentosServicoList: DocumentosServicosArquivo[] = [];

            for await (const arquivo of documentos) {
                const previewName = arquivo?.previewName || "";
                const extension = arquivo?.file?.name?.split(".")?.pop() || "";

                //procura se já existe outro arquivo com mesmo tipo e extensao
                const findIdxAtual = indexes?.find(x=>x.fileType === previewName && x.extension === extension);

                if(!findIdxAtual){
                    indexes.push({
                        fileType: previewName,
                        extension: extension || "",
                        count: 1,
                    })
                }else{
                    findIdxAtual.count++;
                }

                //localiza o registro atualizado para preencher o fileName
                const idxAtual = indexes.find(x=>x.fileType === previewName && x.extension === extension);
                const filename = multiplos.includes(arquivo?.fileType) ? `${idxAtual?.fileType}${idxAtual?.count}.${idxAtual?.extension}` : `${arquivo?.previewName}1.${extension}`;
                documentosServicoList.push({
                    url: response.data[filename]?.path,
                    tipo: arquivo?.fileType || "",
                    status_documento: "pendente"
                });
            }

            const data: DocumentosServicos = {
                documentos_servico: documentosServicoList
            }

            // Faturamento pela Renovigi
            if (faturamentoRenovigi) {
                let statusComissionamento = selectStatusComissionamento?.value || "";
                if (!permiteGerenciamentoPagamento) {
                    statusComissionamento = "documentacao_enviada_processando";
                }

                data.status_comissionamento = statusComissionamento;
                data.data_pagamento_comissionamento = dataPagamentoComissionamento || "";
            }

            const _request: IDataRequest = {
                url: url_post_documento,
                data: data
            }
            const _response = await api.post(_request);
            if (_response.statusCode === STATUS_CODE.OK) {
                notifierStore.setNotifier({
                    title: "Aviso!",
                    content: `Documentos ${selectStatusComissionamento?.value === "documentacao_aprovada" ? 'aprovados' : 'enviados'} com sucesso!`,
                    timeOut: 3500,
                    onFinishTimeout: () => {
                        notifierStore.setOpen(false)
                        onSuccess();
                        onClose();
                    },
                    onClose: () => {
                        notifierStore.setOpen(false)
                        onSuccess();
                        onClose();
                    }
                })
                notifierStore.setOpen(true);
            } else {
                const content:string[] = [];
                if (_response?.message) {
                    if (Array.isArray(_response?.message)) {
                        _response?.message.forEach((m:string) => content.push(m));
                    } else if (typeof _response?.message === "string") {
                        content.push(_response?.message);
                    }
                }
                const mensagemVigencia = content?.includes(MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO) || content?.some((message:any) => message?.includes(MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO));
                
                notifierStore.setNotifier({
                    title: "Aviso!",
                    content: mensagemVigencia ? MENSAGEM_VIGENCIA_SUBSTITUICAO_CONTA_ENCERRADO : "Não foi possível atualizar o status de comissionamento. Arquivos não enviados.",
                    timeOut: 3500,
                    onFinishTimeout: () => {
                        if (mensagemVigencia) window.location.href = `${window.location.origin}/projetos/menu-projetos`;
                        notifierStore.setOpen(false)
                    },
                    onClose: () => {
                        if (mensagemVigencia) window.location.href = `${window.location.origin}/projetos/menu-projetos`;
                        notifierStore.setOpen(false)
                    }
                })
                notifierStore.setOpen(true);
            }
        }
    }

    const parseFile = (anexoFile: AnexoFile, form: FormData): void => {
        const file = anexoFile.file;
        if (file) {
            const extension = anexoFile?.file?.name.split(".").pop();
            const filename = `${anexoFile?.previewName}.${extension}`;
            form.append(filename, file, file.name);
        }
    }

    useEffect(() => {
        setPermiteGerenciamentoPagamento(PermissionUtil.can(PermissionUtil.PERMISSION_GERENCIAR_PAGAMENTO_SERVICO_LISTAGEM, permissions));
        carregarDadosBancarios();
    }, [openModal])

    useEffect(() => {
        const statusComissionamento = projeto?.status_comissionamento || ""
        const status: any = STATUS_COMISSIONAMENTO.find(item => item.value === (statusComissionamento || DEFAULT_STATUS_COMISSIONAMENTO.value));
        setSelectStatusComissionamento(status)
    }, [projeto?.status_comissionamento])

    const onChangeFiles = useCallback((files: AnexoFile[], tipo: DOCUMENTOS_SERVICO_TYPE) => {
        switch (tipo) {
            case DOCUMENTOS_SERVICO_TYPE.ORDEM_SERVICO:
                setOrdemServicoFiles(files)
                break;
            case DOCUMENTOS_SERVICO_TYPE.NF_SERVICO_INSTALACAO:
                setNotaFiscalServicoInstalacaoFiles(files)
                break;
            case DOCUMENTOS_SERVICO_TYPE.IDENTIFICACAO_CLIENTE:
                setIdenficacaoClienteFiles(files)
                break;
            case DOCUMENTOS_SERVICO_TYPE.ART_TRT_PROJETO:
                setArtTrtFiles(files)
                break;
        }
    }, [])

    return <>
        <Dialog
            open={openModal}
            onClose={onClose}
            maxWidth='md'
            className="modal-enviar-doc-servico"
        >
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid container size={12}>
                        <Grid size={12}>
                            <Box component={"h2"}>Documentos de Serviço - <span style={{ fontSize: 15, fontWeight: 'normal' }}>{projeto.codigo_venda}</span></Box>
                        </Grid>
                    </Grid>
                    <Grid container size={12}>
                        <Grid size={12}>
                            <Card style={{border: `1px solid ${theme.palette.error.main}`, padding: theme.spacing(1), color: theme.palette.error.main}}>
                                <div style={{display: "flex", flexDirection: "row"}}>
                                    <div style={{display: "flex", flexShrink: 1, flexGrow: 0, paddingRight: "4px"}}>
                                        <ErrorOutlineIcon />
                                    </div>
                                    <div style={{display: "flex", flexDirection: "column"}}>
                                        <div style={{wordWrap: "break-word"}}>
                                            {dadosBancarios !== null && (
                                                <>
                                                    {dadosBancarios.status ? (
                                                        <>
                                                            <span>{TEXTO_FATURAMENTO}</span><br/>
                                                            {dadosBancarios.message} <br/>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span>Atenção! </span><br />
                                                            <span>{TEXTO_FATURAMENTO}</span><br />
                                                            {dadosBancarios.message}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Grid>
                        {documentos.map((doc) => {
                            return (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <UploadFileDocumentoServico
                                        documents={doc.documentos.map(item => item.nome)}
                                        title={doc.label}
                                        keyFile={doc.tipo}
                                        disabled={doc.status_geral !== STATUS_DOCUMENTO.RECUSADO && doc.documentos.length > 0 && !projeto?.dados_finalizar_renovigi}
                                        toolTipText={doc.tooltipText}
                                        fileType={doc.tipo as string}
                                        onSave={(files: AnexoFile[]) => onChangeFiles(files, doc.tipo)}
                                        arquivoAprovado={doc.status_geral === 'aprovado' && !projeto?.dados_finalizar_renovigi}
                                    />
                                </Grid>
                            )
                        })}
                        {permiteGerenciamentoPagamento && <>
                            <Grid size={12} style={{ display: "flex", marginTop: isMobile ? 15 : 10, flexDirection: isMobile ? "column" : "row" }}>
                                <Grid size={{ xs: 12, md: 6 }} spacing={2}>
                                    <div style={{ paddingRight: isMobile ? 0 : "1%" }}>
                                        <InputComboBoxList
                                            id={`form-status-comissao`}
                                            key={`form-status-comissao`}
                                            disabled={!projeto?.dados_finalizar_renovigi}
                                            options={projeto?.dados_finalizar_renovigi ? STATUS_COMISSIONAMENTO_LISTA : STATUS_COMISSIONAMENTO}
                                            label="Status de Comissionamento "
                                            value={projeto?.dados_finalizar_renovigi ? selectStatusComissionamento : statusComissao}
                                            textField="text"
                                            valueField="value"
                                            renderOptions={(option: any) => {
                                                return option.text;
                                            }}
                                            renderOptionsLabel={(option: any) => {
                                                return option.text;
                                            }}
                                            onChange={setSelectStatusComissionamento}
                                        />
                                    </div>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }} alignContent="flex-end">
                                    <div style={{ paddingLeft: "1%" }}>
                                        <InputDateMui
                                            disabled={!projeto?.dados_finalizar_renovigi}
                                            label={`Pagamento em`}
                                            fullWidth
                                            placeholder="dd/mm/yyyy"
                                            value={dataPagamentoComissionamento}
                                            onChange={(value: any) => {
                                                setDataPagamentoComissionamento(value)
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        </>}
                    </Grid>
                </Grid>
            </DialogContent>
            <FooterBottom className="footer-modal-enviar-doc-servico" style={isMobile ? { display: "flex", flexDirection: "column-reverse", width: "100%" } : {}}>
                <FlatButton
                    color={isMobile ? "secondary" : "inherit"}
                    variant={isMobile ? "outlined" : "contained"}
                    disableElevation
                    text="Cancelar"
                    onClick={onClose}
                    fullWidth={isMobile}
                />
                {projeto?.documentos_servico && PermissionUtil.can(PermissionUtil.PROJETOS_DOCUMENTOS_SERVICO_UPLOADS, permissions)? <>
                    <FlatButton
                        color="secondary"
                        variant="contained"
                        disableElevation
                        text="Baixar Documentos"
                        className="btn-baixar-docs"
                        fullWidth={isMobile}
                        onClick={() => {
                            doDownload();
                        }}
                    />
                </> : ""}
                <FlatButton
                    color="primary"
                    variant="contained"
                    disableElevation
                    text="Confirmar"
                    disabled={!canSubmit}
                    fullWidth={isMobile}
                    onClick={() => {
                        upload();
                    }}
                />
            </FooterBottom>
        </Dialog >
    </>
}

export { ModalEnviarDocumentosServico };